import { Injectable } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';

import { toError } from '@utilities/to-error';
import { environment } from '@environments/environment';

type SplunkLevel = 'info' | 'warn' | 'error';

type SplunkMeta = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class SplunkLoggerService {
  info(message: string, meta?: SplunkMeta): void {
    this.log('info', message, meta);
  }

  warn(message: string, meta?: SplunkMeta): void {
    this.log('warn', message, meta);
  }

  error(error: unknown, meta?: SplunkMeta): void {
    const normalizedError = toError(error);
    this.log('error', normalizedError.message, {
      error: String(normalizedError),
      stack: normalizedError.stack,
      ...meta,
    });
  }

  private log(level: SplunkLevel, message: string, meta?: SplunkMeta): void {
    if (environment.NAME !== 'prod') {
      // eslint-disable-next-line no-console
      console[level]('Logger: ', message, ...(meta ? [JSON.stringify(meta)] : []));
    }

    // Log in non-prod environments by default but limit splunk logging where appropriate
    if (!environment.SPLUNK_ENABLED) return;

    const payload = {
      time: Temporal.Now.instant().epochMilliseconds / 1000,
      source: 'HomeServiceContract',
      sourceType: 'homeservicecontract_json',
      index: 'tes',
      fields: {
        level,
        environment: environment.NAME,
        url: globalThis.location?.pathname,
      },
      event: {
        message,
        ...meta,
      },
    };

    void this.fetchWithRetry(JSON.stringify(payload));
  }

  /*
   * Fire and forget sending logs to Splunk. Retry up to 3 times with exponential backoff
   * First attempt, 0 retries, fetch fails, delay 1s, retry
   * Second attempt, 1 retries, fetch fails, delay 2s, retry
   * Third attempt, 2 retries, fetch fails, delay 4s, retry
   * Fourth attempt, 3 retries, fetch fails, report to new relic
   */
  private async fetchWithRetry(body: string, retries = 0): Promise<void> {
    const MAX_RETRIES = 3;

    try {
      const response = await fetch('https://hec-prod.logs.nationwide.com:45436/services/collector/event', {
        method: 'POST',
        headers: {
          Authorization: 'Splunk 166570bf-5d3d-493c-9363-6d6e278b0048',
          'Content-Type': 'application/json',
        },
        body,
        signal: AbortSignal.timeout(5000),
        keepalive: true,
      });

      if (!response.ok) {
        if (retries < MAX_RETRIES) {
          await this.delay(retries);
          return this.fetchWithRetry(body, retries + 1);
        } else {
          window.newrelic?.noticeError?.(toError('Splunk exceeded max retries'));
        }
      }
    } catch (err) {
      if (retries < MAX_RETRIES) {
        await this.delay(retries);
        return this.fetchWithRetry(body, retries + 1);
      }
      window.newrelic?.noticeError?.(toError(err));
    }
  }

  private delay(retries: number): Promise<void> {
    const ms = 1000 * 2 ** retries;
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
